import * as React from 'react';
import './Accordion.scss';

// Define props directly
interface AccordionProps {
    title: string;
    children: React.ReactNode;
    isOpen: boolean; // Control open state externally
    onToggle: () => void; // Callback when header is clicked
    'data-testid'?: string;
}

const Accordion = (props: AccordionProps & React.HTMLAttributes<HTMLDivElement>) => {
    const { title, children, isOpen, onToggle, 'data-testid': dataTestId, ...restProps } = props;
    // Generate IDs based on title (ensure uniqueness if titles can collide)
    const targetId = `target_${title.replace(/\W+/g, '_')}`; // Replace non-word chars with _
    const labelledById = `labelledBy_${title.replace(/\W+/g, '_')}`;

    // Set collapse container class and aria-expanded based on the isOpen prop
    const collapseClass = isOpen ? 'accordion-collapse collapse show' : 'accordion-collapse collapse';
    const ariaExpanded = isOpen ? 'true' : 'false';
    // Button class depends on state, remove 'collapsed' if open
    const buttonClass = isOpen ? 'accordion-button' : 'accordion-button collapsed';

    return (
        // Use restProps for passing other HTML attributes like className, style, etc.
        <div className="accordion" data-testid={dataTestId} {...restProps}>
            <div className="accordion-item" key={targetId}>
                <h2 className="accordion-header" id={labelledById}>
                    <button
                        className={buttonClass} // Use dynamic button class
                        type="button"
                        // Remove data-bs-toggle/target as it's controlled by React state
                        aria-expanded={ariaExpanded}
                        aria-controls={targetId}
                        onClick={onToggle} // Use the onToggle callback
                    >
                        {title}
                    </button>
                </h2>
                <div id={targetId} className={collapseClass} aria-labelledby={labelledById}>
                    <div className="accordion-body">{children}</div>
                </div>
            </div>
        </div>
    );
};

export default Accordion;
